/**
 * Typography — Tailwind replacement for the legacy `Typography`.
 * `tag` → `as`; the old numeric pixel `size` scale (1..24, 36, 50, 60...) and
 * `font` enum are replaced with a small Tailwind size/weight scale.
 */
import { cn } from '@realtyfeed/utils';
import type { ComponentPropsWithoutRef, ElementType, ReactNode } from 'react';

const sizeClasses = {
  xs: 'text-xs',
  sm: 'text-sm',
  base: 'text-base',
  lg: 'text-lg',
  xl: 'text-xl',
  '2xl': 'text-2xl',
  '3xl': 'text-3xl',
} as const;

const weightClasses = {
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
} as const;

export type TypographySize = keyof typeof sizeClasses;
export type TypographyWeight = keyof typeof weightClasses;

type TypographyOwnProps = {
  as?: ElementType;
  size?: TypographySize;
  weight?: TypographyWeight;
  block?: boolean;
  color?: string;
  children?: ReactNode;
};

export type TypographyProps = TypographyOwnProps &
  Omit<ComponentPropsWithoutRef<'span'>, keyof TypographyOwnProps>;

export function Typography({
  as,
  size = 'base',
  weight = 'normal',
  block,
  color,
  className,
  style,
  children,
  ...props
}: TypographyProps) {
  const Tag = as ?? 'span';

  return (
    <Tag
      className={cn(sizeClasses[size], weightClasses[weight], block && 'block', className)}
      style={color ? { ...style, color } : style}
      {...props}
    >
      {children}
    </Tag>
  );
}
